if (!customElements.get('agency-services-projects')) {
  class AgencyServicesProjects extends HTMLElement {
    connectedCallback() {
      this.mainContent = this.closest('#MainContent') || document.getElementById('MainContent');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.frameId = null;
      this.hydrationFrameId = null;
      this.isActive = null;

      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.updateSurfaceState = this.updateSurfaceState.bind(this);
      this.hydrateBookingControls = this.hydrateBookingControls.bind(this);

      this.hydrateBookingControls();
      this.setupCardGlare();

      window.addEventListener('scroll', this.scheduleUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleUpdate, { passive: true });
      window.addEventListener('orientationchange', this.scheduleUpdate, { passive: true });
      window.addEventListener('pageshow', this.scheduleUpdate, { passive: true });
      window.visualViewport?.addEventListener('resize', this.scheduleUpdate, { passive: true });
      this.reducedMotionQuery.addEventListener?.('change', this.scheduleUpdate);
      document.addEventListener('shopify:section:load', this.hydrateBookingControls);
      document.addEventListener('shopify:section:unload', this.hydrateBookingControls);
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((event) => {
        document.addEventListener(event, this.scheduleUpdate);
      });
      this.surfaceResizeObserver = new ResizeObserver(this.scheduleUpdate);
      if (this.mainContent) this.surfaceResizeObserver.observe(this.mainContent);

      this.scheduleUpdate();
    }

    disconnectedCallback() {
      this.teardownCardGlare?.();
      window.removeEventListener('scroll', this.scheduleUpdate);
      window.removeEventListener('resize', this.scheduleUpdate);
      window.removeEventListener('orientationchange', this.scheduleUpdate);
      window.removeEventListener('pageshow', this.scheduleUpdate);
      window.visualViewport?.removeEventListener('resize', this.scheduleUpdate);
      this.reducedMotionQuery?.removeEventListener?.('change', this.scheduleUpdate);
      document.removeEventListener('shopify:section:load', this.hydrateBookingControls);
      document.removeEventListener('shopify:section:unload', this.hydrateBookingControls);
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((event) => {
        document.removeEventListener(event, this.scheduleUpdate);
      });
      this.surfaceResizeObserver?.disconnect();

      if (this.frameId !== null) cancelAnimationFrame(this.frameId);
      if (this.hydrationFrameId !== null) cancelAnimationFrame(this.hydrationFrameId);
      const remainingSection = this.mainContent?.querySelector('agency-services-projects');
      if (remainingSection) remainingSection.scheduleUpdate?.();
      else this.mainContent?.removeAttribute('data-agency-services-focus');
    }

    setupCardGlare() {
      this.teardownCardGlare?.();
      const controller = new AbortController();
      const { signal } = controller;
      const media = window.matchMedia(
        '(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference) and (forced-colors: none)'
      );
      const entries = [];

      const reset = (entry) => {
        if (entry.frame !== null) cancelAnimationFrame(entry.frame);
        if (entry.timer !== null) clearTimeout(entry.timer);
        entry.frame = null;
        entry.timer = null;
        entry.pointer = null;
        entry.item.classList.remove('is-glare-active', 'is-glare-tracking');
        ['x', 'y', 'bg-x', 'bg-y', 'rotate-x', 'rotate-y'].forEach((property) => {
          entry.card.style.removeProperty(`--agency-glare-${property}`);
        });
      };

      const resetAll = () => entries.forEach(reset);

      this.querySelectorAll('[data-services-glare-item]').forEach((item) => {
        const card = item.querySelector('.agency-services-projects__card');
        if (!card) return;
        const entry = { item, card, frame: null, timer: null, pointer: null };
        entries.push(entry);

        const track = (event) => {
          if (event.pointerType !== 'mouse' || !media.matches) return;
          if (!item.classList.contains('is-glare-active')) {
            item.classList.add('is-glare-active');
            entry.timer = setTimeout(() => {
              entry.timer = null;
              if (item.classList.contains('is-glare-active')) item.classList.add('is-glare-tracking');
            }, 300);
          }
          entry.pointer = { x: event.clientX, y: event.clientY };
          if (entry.frame !== null) return;

          entry.frame = requestAnimationFrame(() => {
            entry.frame = null;
            if (!media.matches || !entry.pointer || !item.isConnected) return reset(entry);
            // Measure the untransformed grid item so tilt cannot move its own hover target.
            const rect = item.getBoundingClientRect();
            if (!rect.width || !rect.height) return reset(entry);
            const x = Math.max(0, Math.min(100, ((entry.pointer.x - rect.left) / rect.width) * 100));
            const y = Math.max(0, Math.min(100, ((entry.pointer.y - rect.top) / rect.height) * 100));
            card.style.setProperty('--agency-glare-x', `${x}%`);
            card.style.setProperty('--agency-glare-y', `${y}%`);
            card.style.setProperty('--agency-glare-bg-x', `${37.5 + x / 4}%`);
            card.style.setProperty('--agency-glare-bg-y', `${33.333333 + y / 3}%`);
            card.style.setProperty('--agency-glare-rotate-x', `${(y - 50) * 0.2}deg`);
            card.style.setProperty('--agency-glare-rotate-y', `${(x - 50) * -0.1142857143}deg`);
          });
        };

        item.addEventListener('pointerenter', track, { signal });
        item.addEventListener('pointermove', track, { signal });
        item.addEventListener('pointerleave', () => reset(entry), { signal });
        item.addEventListener('pointercancel', () => reset(entry), { signal });
      });

      media.addEventListener('change', resetAll, { signal });
      window.addEventListener('blur', resetAll, { signal });
      window.addEventListener('resize', resetAll, { signal, passive: true });
      window.addEventListener('scroll', resetAll, { signal, passive: true });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) resetAll();
      }, { signal });

      this.teardownCardGlare = () => {
        resetAll();
        controller.abort();
      };
    }

    scheduleUpdate() {
      if (this.frameId !== null) return;

      this.frameId = requestAnimationFrame(this.updateSurfaceState);
    }

    hydrateBookingControls() {
      if (this.hydrationFrameId !== null) cancelAnimationFrame(this.hydrationFrameId);
      this.hydrationFrameId = requestAnimationFrame(() => {
        this.hydrationFrameId = null;
        if (!this.isConnected) return;
        const drawer = document.querySelector('agency-contact-drawer [data-contact-drawer]');
        this.querySelectorAll('[data-contact-drawer-open][data-contact-drawer-route]').forEach((trigger) => {
          if (drawer?.id) trigger.setAttribute('aria-controls', drawer.id);
          else trigger.removeAttribute('aria-controls');
        });
      });
    }

    updateSurfaceState() {
      this.frameId = null;
      if (!this.mainContent) return;

      const viewportHeight = window.visualViewport?.height || document.documentElement.clientHeight;
      const viewportMidpoint = viewportHeight * 0.5;
      const viewportExitLine = viewportHeight * 0.4;
      const reviews = Array.from(this.mainContent.querySelectorAll('agency-review-highlights'))
        .filter((section) => section.getClientRects().length && section.getBoundingClientRect().height > 0);
      const isActive = Array.from(this.mainContent.querySelectorAll('agency-services-projects')).some((section) => {
        const rect = section.getBoundingClientRect();
        if (!section.getClientRects().length || rect.height <= 0) return false;
        const reviewSection = reviews.find((review) =>
          section.compareDocumentPosition(review) & Node.DOCUMENT_POSITION_FOLLOWING
        );
        const exitBottom = reviewSection ? reviewSection.getBoundingClientRect().bottom : rect.bottom;
        return rect.top <= viewportMidpoint && exitBottom > viewportExitLine;
      });

      if (isActive === this.isActive) return;
      this.isActive = isActive;
      if (isActive) this.mainContent.setAttribute('data-agency-services-focus', 'true');
      else this.mainContent.removeAttribute('data-agency-services-focus');
    }
  }

  customElements.define('agency-services-projects', AgencyServicesProjects);
}
